import { useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import { visuallyHidden } from '@mui/utils'
import AssistantPanel from './AssistantPanel'
import { useAssistant } from './AssistantProvider'
import { ASSISTANT_NAME } from './copy'

/**
 * Ask Attorney Share's docked panel. Mounted once in RootLayout (flag on
 * only), so on wider screens it stays open from page to page; its button
 * lives in the top bar (AssistantTopBar), which each page renders. Closing
 * the panel hands focus back to that button. Hidden when signed out and on
 * the full /assistant page.
 */
const AssistantDock = () => {
  const assistant = useAssistant()
  const isPanelOpen = Boolean(assistant?.isPanelOpen)
  const launcherRef = assistant?.launcherRef

  // Set when the user closes the panel; focus moves once it has closed.
  const restoreFocus = useRef(false)
  useEffect(() => {
    // A close tapped twice can leave the flag set; start each opening clean.
    if (isPanelOpen) {
      restoreFocus.current = false
      return
    }
    if (!restoreFocus.current) return
    restoreFocus.current = false
    launcherRef?.current?.focus()
  }, [isPanelOpen, launcherRef])

  // The nudge appears without focus moving, so say each one aloud once,
  // where the top bar is there to act on it: under the button, or as "Needs
  // you" while its field is open. A few pages have no top bar; moving to one
  // that does tries again.
  const hasTopBar = Boolean(assistant?.hasTopBar)
  const nudge = assistant?.isAvailable ? assistant.nudge : null
  const announced = useRef(new Set<string>())
  const [announcement, setAnnouncement] = useState('')
  useEffect(() => {
    if (!nudge || !hasTopBar || announced.current.has(nudge.id)) return
    announced.current.add(nudge.id)
    setAnnouncement(`${ASSISTANT_NAME}: ${nudge.message}`)
  }, [nudge, hasTopBar])

  if (!assistant) return null

  return (
    <>
      <Box
        role="status"
        aria-live="polite"
        aria-atomic="true"
        data-testid="assistant-nudge-announcer"
        sx={visuallyHidden}
      >
        {announcement}
      </Box>
      {assistant.isAvailable && (
        <AssistantPanel
          onDismiss={() => {
            restoreFocus.current = true
          }}
        />
      )}
    </>
  )
}

export default AssistantDock
